import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Moon, ChevronRight, Zap } from 'lucide-react';

interface TacticalSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    onNavigate: (view: string) => void;
    onToggleSleep: () => void;
    onExit: () => void;
    activeRecoverySession: any;
}

/**
 * TacticalSidebar: Isolated navigation and recovery control center.
 * ADHERES TO REFACOR-PRO SOC MANDATE.
 */
export const TacticalSidebar: React.FC<TacticalSidebarProps> = ({
    isOpen,
    onClose,
    onNavigate,
    onToggleSleep,
    onExit,
    activeRecoverySession
}) => {
    return (
        <AnimatePresence>
            {isOpen && (
                <div style={{ 
                    position: 'fixed', 
                    top: 0, bottom: 0, left: '50%',
                    transform: 'translateX(-50%)',
                    width: '100%',
                    maxWidth: '500px',
                    zIndex: 6000 
                }}>
                    {/* BACKDROP */}
                    <motion.div 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        exit={{ opacity: 0 }} 
                        onClick={onClose} 
                        style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)', zIndex: 6000 }} 
                    />

                    {/* DRAWER */}
                    <motion.div 
                        initial={{ x: '100%' }} 
                        animate={{ x: 0 }} 
                        exit={{ x: '100%' }} 
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }} 
                        style={{ 
                            position: 'absolute', top: 0, right: 0, bottom: 0, width: '280px', 
                            backgroundColor: '#050505', borderLeft: '1px solid #222', 
                            zIndex: 6001, padding: '25px', display: 'flex', flexDirection: 'column' 
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
                            <h2 style={{ fontSize: '14px', fontWeight: '900', color: '#fff', textTransform: 'uppercase', letterSpacing: '2px' }}>
                                TACTICAL <span style={{ color: '#f29b11' }}>HUB</span>
                            </h2>
                            <button onClick={onClose} style={{ all: 'unset', cursor: 'pointer' }}>
                                <X size={24} color="#444" />
                            </button>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            
                            {/* METABOLIC HUB */}
                            <button onClick={() => { onNavigate('metabolic-hub'); onClose(); }} style={itemStyle}>
                                <div style={{ ...iconPodStyle, backgroundColor: 'rgba(168, 85, 247, 0.1)' }}>
                                    <Zap size={20} color="#a855f7" />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={labelStyle}>METABOLIC HUB</div>
                                    <div style={subLabelStyle}>Energy Balance & Coaching</div>
                                </div>
                                <ChevronRight size={16} color="#222" />
                            </button>

                            {/* BIOMETRICS */}
                            <button onClick={() => { onNavigate('profile'); onClose(); }} style={itemStyle}>
                                <div style={{ ...iconPodStyle, backgroundColor: 'rgba(242, 155, 17, 0.1)' }}>
                                    <User size={20} color="#f29b11" />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={labelStyle}>METABOLIC PROFILE</div>
                                    <div style={subLabelStyle}>Biometrics & TDEE</div>
                                </div>
                                <ChevronRight size={16} color="#222" />
                            </button>

                            {/* SLEEP/RECOVERY */}
                            <button onClick={() => { onToggleSleep(); onClose(); }} style={itemStyle}>
                                <div style={{ 
                                    ...iconPodStyle, 
                                    backgroundColor: activeRecoverySession ? 'rgba(59, 130, 246, 0.1)' : 'rgba(255,255,255,0.05)' 
                                }}>
                                    <Moon size={20} color={activeRecoverySession ? '#3b82f6' : '#444'} fill={activeRecoverySession ? '#3b82f6' : 'transparent'} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={labelStyle}>{activeRecoverySession ? 'WAKE UP' : 'INITIATE RECOVERY'}</div>
                                    <div style={subLabelStyle}>{activeRecoverySession ? 'Finalize Sleep' : 'Start Sleep Tracking'}</div>
                                </div>
                                <ChevronRight size={16} color="#222" />
                            </button>
                        </div>

                        <div style={{ marginTop: 'auto' }}>
                            <button onClick={onExit} style={{ all: 'unset', cursor: 'pointer', width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #222', textAlign: 'center', fontSize: '10px', fontWeight: '900', color: '#444', letterSpacing: '1px' }}>
                                EXIT TO SYSTEM HUB
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

const itemStyle: React.CSSProperties = {
    all: 'unset', cursor: 'pointer', padding: '16px', borderRadius: '12px', 
    backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid #111', 
    display: 'flex', alignItems: 'center', gap: '15px'
};

const iconPodStyle: React.CSSProperties = {
    width: '40px', height: '40px', borderRadius: '50%', 
    display: 'flex', alignItems: 'center', justifyContent: 'center'
};

const labelStyle: React.CSSProperties = { fontSize: '12px', fontWeight: '900', color: '#fff' };
const subLabelStyle: React.CSSProperties = { fontSize: '9px', color: '#444', fontWeight: '700' };
